import React, { useCallback, useEffect, useState } from "react";
import { Row } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import {
  getAllPhysician,
  getPhysicianTimeSlots,
} from "../../../../store/slices/physicianSlice";
import { getAllSpecialization } from "../../../../store/slices/specializationSlice";
import Select from "react-select";

const Consultation = ({
  formData,
  setFormData,
  selectedTimeOfTheDay,
  setSelectedTimeOfTheDay,
}) => {
  const [selectedSpecialization, setSelectedSpecialization] = useState(null);
  const [selectedConsultationType, setSelectedConsultationType] =
    useState(null);
  const [physicianFees, setPhysicianFees] = useState({});
  const [selectedPhysician, setSelectedPhysician] = useState(null);
  const { physician, physicianTimeSlot } = useSelector(
    (state) => state.physician,
  );
  const [availableSlots, setAvailableSlots] = useState([]);
  const { clinicInfo } = useSelector((state) => state.clinic);
  const { specializations } = useSelector((state) => state.specializations);
  const { token } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [availableDays, setAvailableDays] = useState([]);
  const clinicId = clinicInfo?.[0]?.clinicId || null;
  useEffect(() => {
    dispatch(getAllSpecialization({ token }));
  }, [dispatch, token]);

  useEffect(() => {
    if (selectedSpecialization) {
      setSelectedPhysician(null);
      setSelectedTimeOfTheDay(null);
      setAvailableSlots([]);
      setPhysicianFees({});

      setFormData((prevData) => ({
        ...prevData,
        physicianId: null,
        physicianName: "",
        timeOfTheDay: null,
        slotTime: null,
        consultationFee: 0,
      }));

      dispatch(
        getAllPhysician({
          specializationId: selectedSpecialization?.value,
          clinicId,
          clinicStatus: "Accepted",
          physicianStatus: "Accepted",
          token,
        }),
      );
    }
  }, [
    dispatch,
    selectedSpecialization,
    token,
    clinicId,
    setFormData,
    setSelectedTimeOfTheDay,
  ]);

  const consultationType = [
    { value: "Video Consultation", label: "Video Consultation" },
    { value: "Clinic Visit", label: "Clinic Visit" },
  ];

  const appointmentTypeOptions = {
    videoConsultation: [{ value: "Video", label: "Video" }],
    clinicVisit: [
      { value: "regular", label: "Regular" },
      { value: "followup", label: "Follow-up" },
      { value: "emergency", label: "Emergency" },
      { value: "free", label: "Free" },
    ],
  };

  const getConsultationTypeOptions = () => {
    if (selectedConsultationType === "Video Consultation") {
      return appointmentTypeOptions.videoConsultation;
    } else if (selectedConsultationType === "Clinic Visit") {
      return appointmentTypeOptions.clinicVisit;
    }
    return [];
  };

  const handlePhysicianSelect = (selectedOption) => {
    setSelectedPhysician(selectedOption);

    const selectedPhysicianData = physician?.find(
      (doc) => doc?._id === selectedOption?.value,
    );

    const clinics = selectedPhysicianData?.asociatedClinic || [];

    if (clinics?.length > 0) {
      const consultationFees = {
        regular: clinics[0]?.regularFees,
        emergency: clinics[0]?.emergencyFees,
        followup: clinics[0]?.followupFees,
        video: clinics[0]?.videConsultationFees,
        establishment: clinics[0]?.establishmentFees,
      };

      setPhysicianFees({
        regularFees: consultationFees?.regular,
        emergencyFees: consultationFees?.emergency,
        followupFees: consultationFees?.followup,
        videoConsultationFees: consultationFees?.video,
        establishmentFees: consultationFees?.establishment,
      });
    }

    setFormData((prevData) => ({
      ...prevData,
      physicianId: selectedOption?.value,
      physicianName: selectedOption?.label,
    }));
  };

  const getCurrentTime = useCallback(() => {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, "0")}:${now
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
  }, []);

  // ✅ SLOT FIX ONLY
  const isSlotDisabled = useCallback(
    (slotTime) => {
      if (!formData.appDate) return false;

      const today = new Date();
      const selectedDate = new Date(formData.appDate);

      const isToday =
        today.getFullYear() === selectedDate.getFullYear() &&
        today.getMonth() === selectedDate.getMonth() &&
        today.getDate() === selectedDate.getDate();

      if (!isToday) return false;

      const currentTime = getCurrentTime();

      // ✅ FIX → compare only start time
      const slotStartTime = slotTime?.split(" - ")[0];

      return slotStartTime < currentTime;
    },
    [getCurrentTime, formData.appDate],
  );

  useEffect(() => {
    if (selectedPhysician?.value && formData?.appDate) {
      dispatch(
        getPhysicianTimeSlots({
          clinicId,
          physicianId: selectedPhysician?.value,
          appointmentDate: formData?.appDate,
          token,
        }),
      ).then((response) => {
        const bookedSlotsData =
          response?.payload?.result?.list?.bookedSlots || [];
        const timeOfTheDay =
          response?.payload?.result?.list?.timeSlot?.timeOfTheDay;

        const selectedSession = timeOfTheDay?.find(
          (session) => session._id === selectedTimeOfTheDay?.value,
        );

        const timeSlots = selectedSession
          ? selectedSession?.sessionTime?.map((sessionTime) => ({
              value: sessionTime?._id,
              label: sessionTime?.slotTime,
              isDisabled: isSlotDisabled(sessionTime?.slotTime),
              isBooked: bookedSlotsData.some(
                (item) => item?._id === sessionTime?._id,
              ),
            }))
          : [];

        setAvailableSlots(timeSlots);
      });
    }
  }, [
    dispatch,
    selectedPhysician,
    formData.appDate,
    clinicId,
    selectedTimeOfTheDay,
    isSlotDisabled,
    token,
  ]);

  const handleTimeOfTheDay = (selectedOption) => {
    setSelectedTimeOfTheDay({
      value: selectedOption?.value,
      label: selectedOption?.label,
    });

    setFormData((prevData) => ({
      ...prevData,
      timeOfTheDay: selectedOption?.value,
    }));
  };

  const handleTimeSlot = (selectedOption) => {
    setFormData((prevData) => ({
      ...prevData,
      slotTime: selectedOption?.value,
    }));
  };

  const handleConsultationTypeSelect = (selectedOption) => {
    setSelectedConsultationType(selectedOption?.value);

    setFormData((prevData) => ({
      ...prevData,
      consultationType: selectedOption?.value,
      appointmentType: null,
      consultationFee: 0,
    }));
  };

  const handleAppointmentTypeChange = (selectedOption) => {
    const fee = calculateFee(selectedOption?.value);

    setFormData((prev) => ({
      ...prev,
      appointmentType: selectedOption?.value,
      consultationFee: fee,
    }));
  };

  const calculateFee = (type) => {
    switch (type) {
      case "regular":
        return physicianFees?.regularFees || 0;
      case "emergency":
        return physicianFees?.emergencyFees || 0;
      case "followup":
        return physicianFees?.followupFees || 0;
      case "Video":
        return physicianFees?.videoConsultationFees || 0;
      case "free":
        return 0;
      default:
        return 0;
    }
  };

  return (
    <>
      <Row>
        <div className="col-xl-6">
          <div className="form-group">
            <label htmlFor="name" className="col-form-label">
              Specialization:
            </label>
            <Select
              options={
                specializations &&
                specializations?.map((specialization) => ({
                  value: specialization?._id,
                  label: specialization?.specialization,
                }))
              }
              onChange={(selectedOption) => {
                setSelectedSpecialization(selectedOption);
                setFormData((prevData) => ({
                  ...prevData,
                  specialization: selectedOption?.value,
                  specializationName: selectedOption?.label,
                }));
              }}
              value={{
                value: formData?.specialization,
                label: formData?.specializationName,
              }}
              styles={{
                container: (provided) => ({ ...provided, marginTop: 8 }),
              }}
            />
          </div>
        </div>
        <div className="col-xl-6">
          <div className="form-group">
            <label htmlFor="name" className="col-form-label">
              Consultation Type:
            </label>
            <Select
              options={consultationType}
              onChange={handleConsultationTypeSelect}
              value={
                consultationType &&
                consultationType?.find(
                  (option) => option.value === formData.consultationType,
                )
              }
              styles={{
                container: (provided) => ({ ...provided, marginTop: 8 }),
              }}
            />
          </div>
        </div>
        <div className="col-xl-6">
          <div className="form-group">
            <label htmlFor="name" className="col-form-label">
              Physician:
            </label>
            <Select
              options={
                physician &&
                physician?.map((physician) => ({
                  value: physician?._id,
                  label: physician?.userId?.name,
                }))
              }
              value={{
                value: formData?.physicianId,
                label: formData?.physicianName,
              }}
              onChange={handlePhysicianSelect}
              styles={{
                container: (provided) => ({ ...provided, marginTop: 8 }),
              }}
            />
          </div>
        </div>
        <div className="col-xl-6">
          <div className="form-group">
            <label htmlFor="consultationType" className="col-form-label">
              Appointment Type:
            </label>
            <Select
              options={getConsultationTypeOptions()}
              value={
                getConsultationTypeOptions()?.find(
                  (option) => option.value === formData.appointmentType,
                ) || null
              }
              styles={{
                container: (provided) => ({ ...provided, marginTop: 8 }),
              }}
              onChange={handleAppointmentTypeChange}
            />
          </div>
        </div>
        {/* Time Slot */}
        <div className="col-xl-6">
          <div className="form-group">
            <label htmlFor="name" className="col-form-label">
              Time Of The Day:
            </label>
            <Select
              value={
                selectedTimeOfTheDay
                  ? {
                      value: selectedTimeOfTheDay.value,
                      label: selectedTimeOfTheDay.label,
                    }
                  : null
              }
              options={
                physicianTimeSlot?.timeSlot?.timeOfTheDay?.map(
                  (timeOfTheDay) => ({
                    value: timeOfTheDay?._id,
                    label: timeOfTheDay?.sessionName,
                  }),
                ) || []
              }
              onChange={handleTimeOfTheDay}
              styles={{
                container: (provided) => ({ ...provided, marginTop: 8 }),
              }}
            />
          </div>
        </div>
        {/* Slot */}
        <div className="col-xl-6">
          <div className="form-group">
            <label htmlFor="name" className="col-form-label">
              Slot:
            </label>
            <Select
              value={
                formData.slotTime
                  ? {
                      value: formData.slotTime,
                      label:
                        availableSlots.find(
                          (slot) => slot.value === formData.slotTime,
                        )?.label || "Select Slot",
                    }
                  : null
              }
              options={availableSlots.map((slot) => ({
                value: slot?.value,
                label: `${slot?.label} ${slot?.isBooked ? "(Booked)" : ""}`,
                isDisabled: slot?.isDisabled || slot?.isBooked,
              }))}
              onChange={handleTimeSlot}
              isDisabled={!availableSlots.length}
              styles={{
                container: (provided) => ({ ...provided, marginTop: 8 }),
              }}
            />
          </div>
        </div>
        {/* Consultation Fee */}
        <div className="col-xl-6">
          <div className="form-group">
            <label htmlFor="name" className="col-form-label">
              Consultation Fee :
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Consultation Fee"
              value={formData?.consultationFee}
              onChange={(e) =>
                setFormData((prevData) => ({
                  ...prevData,
                  consultationFee: e.target.value,
                }))
              }
              readOnly
            />
          </div>
        </div>
      </Row>
    </>
  );
};

export default Consultation;
